import React, { useMemo } from "react";

/* -------------------- Zawal = Dhuhr - 15 min helpers (module-scope) -------------------- */
function parseTimeToMinutes(t) {
  if (!t || t === "—") return null;

  const s = String(t).trim();
  if (!s) return null;

  // Matches: "1:05 PM", "1:05PM", "13:05", "01:05 am", etc.
  const m = s.match(/(\d{1,2})\s*:\s*(\d{2})\s*([AaPp]\s*\.?\s*[Mm]\.?)?/);
  if (!m) return null;

  let hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const mer = m[3]
    ? m[3].replace(/\./g, "").replace(/\s/g, "").toLowerCase() // "am" | "pm"
    : null;

  // Convert to 24h
  if (mer) {
    if (hh === 12) hh = mer === "am" ? 0 : 12;
    else if (mer === "pm") hh += 12;
  }

  return { minutes: hh * 60 + mm, hasMeridiem: !!mer };
}

function formatMinutes(totalMinutes, preferMeridiem) {
  const mins = ((totalMinutes % 1440) + 1440) % 1440; // clamp 0..1439
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;

  if (preferMeridiem) {
    const suffix = h24 >= 12 ? "PM" : "AM";
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
  }

  return `${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function getZawalFromDhuhr(dhuhrTime, minusMinutes = 15) {
  const parsed = parseTimeToMinutes(dhuhrTime);
  if (!parsed) return "—";
  return formatMinutes(parsed.minutes - minusMinutes, parsed.hasMeridiem);
}

const Footer = ({
  timingColor,
  textColor,
  backgroundColor,
  highlightColor,
  prayerData,
  nextEvent,
}) => {
  const { sunrise, sunset, zawal, jummahList } = useMemo(() => {
    if (!prayerData) return { sunrise: "—", sunset: "—", zawal: "—", jummahList: [] };

    const timings = prayerData.startTimes?.timings || {};
    const list = Array.isArray(prayerData.jummahTimes) ? [...prayerData.jummahTimes] : [];

    // Sort Jummah by sequence
    list.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    return {
      sunrise: timings.sunrise || "—",
      sunset: timings.sunset || "—",
      // ✅ Zawal = 15 minutes before Dhuhr
      zawal: getZawalFromDhuhr(timings.dhuhr, 15),
      jummahList: list,
    };
  }, [prayerData]);

  const format = (t) => {
    if (!t || t === "—") return "—";
    return t.replace(/\s?(AM|PM)/i, "").replace(/^0/, "");
  };

  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const cleanTime = (t) => t?.replace(/\s/g, "").replace(/^0/, "").toLowerCase();

  return (
    <div
      className="w-full flex justify-between items-center h-full px-[0.2svh] text-center py-[0.5svh]"
      style={{
        backgroundColor: backgroundColor || "transparent",
        color: textColor,
        borderRadius: "1svh",
      }}
    >
      {/* Sunrise */}
      <div className="flex flex-col items-center">
        <div className="text-[2svh] leading-tight opacity-80" style={{ color: "inherit" }}>
          Sunrise
        </div>
        <div className="font-[Digital-7] text-[4.7svh] leading-none my-1" style={{ color: timingColor }}>
          {format(sunrise)}
        </div>
        <div className="text-[1.9svh]" style={{ color: "inherit" }}>
          اشراق
        </div>
      </div>

      {/* Sunset */}
      <div className="flex flex-col items-center">
        <div className="text-[2svh] leading-tight opacity-80" style={{ color: "inherit" }}>
          Sunset
        </div>
        <div className="font-[Digital-7] text-[4.7svh] leading-none my-1" style={{ color: timingColor }}>
          {format(sunset)}
        </div>
        <div className="text-[1.9svh]" style={{ color: "inherit" }}>
          غروب
        </div>
      </div>

      {/* Zawal */}
      <div className="flex flex-col items-center">
        <div className="text-[2svh] leading-tight opacity-80" style={{ color: "inherit" }}>
          Zawal
        </div>
        <div className="font-[Digital-7] text-[4.7svh] leading-none my-1 pl-[0.8svh]" style={{ color: timingColor }}>
          {format(zawal)}
        </div>
        <div className="text-[1.9svh]" style={{ color: "inherit" }}>
          زوال
        </div>
      </div>

      {/* Dynamic Jummah Loop */}
      {jummahList.map((jummah, index) => {
        const isNext =
          nextEvent?.type === "jummah" &&
          cleanTime(nextEvent?.time) === cleanTime(jummah.iqama_Time || jummah.iqama_time);

        return (
          <div
            key={index}
            className="flex flex-col items-center rounded py-1 transition-all duration-300"
            style={{ backgroundColor: isNext ? highlightColor : "transparent" }}
          >
            <div className="text-[2svh] leading-tight opacity-80" style={{ color: "inherit" }}>
              {index === 0 ? "1st Jumu'ah" : `${getOrdinal(index + 1)} Jumu'ah`}
            </div>
            <div className="font-[Digital-7] text-[4.7svh] leading-none my-1" style={{ color: timingColor }}>
              {format(jummah.iqama_Time || jummah.iqama_time)}
            </div>
            <div className="text-[1.9svh]" style={{ color: "inherit" }}>
              {index === 0 ? "جمعہ اول" : index === 1 ? "جمعہ دوم" : "جمعہ سوم"}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Footer;
